import React from 'react';
import {
    Callout,
    DefaultButton,
    DirectionalHint,
    keyframes,
    Link,
    mergeStyles,
    Stack,
    Text,
    useTheme,
} from '@fluentui/react';
import { toast } from 'react-toastify';
import useSettingFocus from '../../hooks/useSettingFocus';

/** Above Fluent's Layer stack (1,000,000) so the ring is visible over Panels and Dialogs. */
const RING_Z_INDEX = 1000001;
const RING_PADDING = 6;

const pulse = keyframes({
    '0%': { opacity: 1 },
    '50%': { opacity: 0.45 },
    '100%': { opacity: 1 },
});

/**
 * Draws attention to the setting named by `?focus=` in the URL.
 *
 * Mounted once per shell. Renders nothing until a focused setting has actually been found on the
 * page, so an ordinary page load costs a single URLSearchParams read.
 */
/** Marks the callout's subtree for the guard below. */
const CALLOUT_ATTRIBUTE = 'data-setting-callout';

/**
 * Stops a click inside the callout from closing whatever the setting is sitting in.
 *
 * Fluent's `Panel` binds `_dismissOnOuterClick` to `document.body` mousedown in the CAPTURE phase
 * and dismisses whenever the target is not inside the panel element. The callout renders in its own
 * Layer, so every click in it is "outside" - clicking "Got it" on a setting in the Search Settings
 * panel closed the panel out from under it.
 *
 * That handler skips when `ev.defaultPrevented`, so preventing the default is the intended escape
 * hatch. It has to happen in the capture phase and BEFORE Fluent's listener: same target and phase
 * means registration order decides, and a React synthetic handler is far too late. Registering once
 * at app start - this host mounts with the shell, long before any panel opens - puts this first.
 */
const useCalloutOuterClickGuard = () => {
    React.useEffect(() => {
        const guard = (ev: MouseEvent) => {
            const target = ev.target as Element | null;
            if (target?.closest?.(`[${CALLOUT_ATTRIBUTE}]`)) {
                ev.preventDefault();
            }
        };
        document.body.addEventListener('mousedown', guard, true);
        return () => document.body.removeEventListener('mousedown', guard, true);
    }, []);
};

const SettingFocusHost: React.FC = () => {
    const theme = useTheme();
    const { setting, focusId, element, rect, dismiss } = useSettingFocus();
    useCalloutOuterClickGuard();

    const ringClass = React.useMemo(
        () =>
            mergeStyles({
                position: 'fixed',
                pointerEvents: 'none',
                zIndex: RING_Z_INDEX,
                borderRadius: 4,
                border: `2px solid ${theme.palette.themePrimary}`,
                boxShadow: `0 0 0 4px ${theme.palette.themeLighter}`,
                animationName: pulse,
                animationDuration: '1.6s',
                animationIterationCount: '3',
            }),
        [theme]
    );

    if (!rect || !element) return null;

    const openDocs = () => {
        if (setting?.docsUrl) {
            window.open(setting.docsUrl, '_blank', 'noopener');
        } else if (setting?.beaconArticleId && window.Beacon) {
            window.Beacon('article', setting.beaconArticleId);
        }
    };

    /**
     * Copies the current location with `focus` set, rather than the stable `/go/setting/` form.
     *
     * This is the support path: whatever context got you here - which storefront, which section's
     * style editor is open, which tab - is already in the URL, and some of it cannot be expressed in
     * a `/go` link at all (a section needs an instance on top of the storefront). The stable form,
     * for documentation that should survive the screen moving, is on the Settings Reference page.
     */
    const copyLink = async () => {
        const id = setting?.id ?? focusId;
        if (!id) return;
        const params = new URLSearchParams(window.location.search);
        params.set('focus', id);
        const link = `${window.location.origin}${window.location.pathname}?${params.toString()}`;
        try {
            await navigator.clipboard.writeText(link);
            toast.success('Link copied');
        } catch {
            toast.error('Could not copy the link to the clipboard');
        }
    };

    const hasDocs = Boolean(setting?.docsUrl || setting?.beaconArticleId);

    return (
        <>
            <div
                className={ringClass}
                style={{
                    left: rect.left - RING_PADDING,
                    top: rect.top - RING_PADDING,
                    width: rect.width + RING_PADDING * 2,
                    height: rect.height + RING_PADDING * 2,
                }}
            />
            <Callout
                target={element.getBoundingClientRect().height > 0 ? element : { x: rect.left, y: rect.bottom }}
                directionalHint={DirectionalHint.bottomLeftEdge}
                onDismiss={dismiss}
                isBeakVisible={true}
                setInitialFocus={false}
                /*
                 * A callout dismisses itself on scroll, resize and lost focus by default, and this
                 * one arrives while the page is still settling - late-loading rows, a panel
                 * animating open, focus landing in the field itself. Any of those cancelled the
                 * highlight a moment after it appeared. Only an explicit dismissal should end it:
                 * "Got it", Escape, or clicking somewhere else.
                 */
                preventDismissOnScroll={true}
                preventDismissOnResize={true}
                preventDismissOnLostFocus={true}
                styles={{ root: { maxWidth: 320 } }}
            >
                {/* The attribute is what useCalloutOuterClickGuard looks for. */}
                <Stack
                    {...{ [CALLOUT_ATTRIBUTE]: true }}
                    tokens={{ childrenGap: '0.5em' }}
                    styles={{ root: { padding: '1em' } }}
                >
                    <Text variant='mediumPlus' styles={{ root: { fontWeight: 600 } }}>
                        {setting?.label ?? focusId}
                    </Text>
                    {setting?.description && <Text variant='small'>{setting.description}</Text>}
                    <Stack horizontal tokens={{ childrenGap: '0.5em' }} verticalAlign='center'>
                        {hasDocs && (
                            <Link onClick={openDocs}>Learn more</Link>
                        )}
                        <Link onClick={copyLink}>Copy link</Link>
                    </Stack>
                    <DefaultButton text='Got it' onClick={dismiss} />
                </Stack>
            </Callout>
        </>
    );
};

export default SettingFocusHost;
